/**
 * Shared names for the auth / onboarding funnel beacon. No logger / navigator —
 * imported by both the browser helper and the `/api/telemetry-beacon` route.
 *
 * Every analytics event listed here is mirrored from Statsig into an OTel
 * counter so Better Stack can chart and alert on it. Statsig events are not
 * queryable as metrics, which is why the auth dashboard had login FAILURES
 * (`frontend.auth_login_error_total`) but no login SUCCESSES and therefore no
 * success rate, and no signup funnel at all.
 *
 * `labels` maps a property on the analytics payload to the metric label it
 * becomes; `fixed` labels are stamped on every sample. Anything not listed
 * never leaves the browser — user ids, durations and free text stay out of
 * metric labels on purpose (cardinality and PII).
 */

export const FUNNEL_BEACON_EVENT = 'frontend.funnel';

export const AUTH_LOGIN_COUNTER = 'frontend.auth_login_total';
export const ONBOARDING_COUNTER = 'frontend.onboarding_total';
/** Session lifecycle, both boundaries: Morfi cookie session (restored /
 *  restore_error / logout / logout_error) and the Turnkey signing session
 *  (refreshed / refresh_failed / expired / reconcile_logout). */
export const SESSION_COUNTER = 'frontend.session_total';
/** Every proactive Morfi-session renewal attempt by the SDK scheduler. */
export const SESSION_REFRESH_COUNTER = 'frontend.session_refresh_total';
/** Private-key / wallet exports through the Turnkey modal. */
export const WALLET_EXPORT_COUNTER = 'frontend.wallet_export_total';
/** Wallets a session cannot sign for because their key lives in another
 *  Turnkey sub-organization, and the reconcile runs it declined for the same
 *  reason. `kind` separates the two; the sub-org ids never become labels. */
export const WALLET_REACHABILITY_COUNTER = 'frontend.wallet_reachability_total';

export interface FunnelMirror {
  readonly counter: string;
  readonly fixed: Readonly<Record<string, string>>;
  readonly labels: Readonly<Record<string, string>>;
}

export const FUNNEL_MIRRORS: Readonly<Record<string, FunnelMirror>> = {
  onramp_funnel: {
    counter: 'frontend.onramp_funnel_total',
    fixed: { schema_version: '1' },
    labels: {
      stage: 'stage',
      outcome: 'outcome',
      method: 'method',
      surface: 'surface',
      errorCode: 'error_code',
    },
  },
  wallet_unreachable_sub_org: {
    counter: WALLET_REACHABILITY_COUNTER,
    fixed: { kind: 'unreachable_wallets' },
    labels: {},
  },
  wallet_reconcile_skipped_foreign_sub_org: {
    counter: WALLET_REACHABILITY_COUNTER,
    fixed: { kind: 'foreign_session' },
    labels: {},
  },
  auth_method_used: {
    counter: AUTH_LOGIN_COUNTER,
    fixed: {},
    labels: { method: 'method', isNewUser: 'new_user' },
  },
  onboarding_started: {
    counter: ONBOARDING_COUNTER,
    fixed: { step: 'started' },
    labels: { source: 'source' },
  },
  onboarding_stage_viewed: {
    counter: ONBOARDING_COUNTER,
    fixed: { step: 'stage_viewed' },
    labels: { stage: 'stage', resumed: 'resumed' },
  },
  onboarding_stage_completed: {
    counter: ONBOARDING_COUNTER,
    fixed: { step: 'stage_completed' },
    labels: { stage: 'stage', skipped: 'skipped' },
  },
  onboarding_completed: {
    counter: ONBOARDING_COUNTER,
    fixed: { step: 'completed' },
    labels: { skippedExplainer: 'skipped' },
  },
  onboarding_exited: {
    counter: ONBOARDING_COUNTER,
    fixed: { step: 'exited' },
    labels: { exitStage: 'stage' },
  },
  session_restored: {
    counter: SESSION_COUNTER,
    fixed: { event: 'restored' },
    labels: {},
  },
  session_restore_error: {
    counter: SESSION_COUNTER,
    fixed: { event: 'restore_error' },
    labels: {},
  },
  logout: {
    counter: SESSION_COUNTER,
    fixed: { event: 'logout' },
    labels: {},
  },
  logout_error: {
    counter: SESSION_COUNTER,
    fixed: { event: 'logout_error' },
    labels: {},
  },
  turnkey_session_refreshed: {
    counter: SESSION_COUNTER,
    fixed: { event: 'refreshed' },
    labels: { trigger: 'trigger', pruned: 'pruned' },
  },
  turnkey_refresh_failed: {
    counter: SESSION_COUNTER,
    fixed: { event: 'refresh_failed' },
    labels: { trigger: 'trigger', reason: 'reason' },
  },
  turnkey_session_expired: {
    counter: SESSION_COUNTER,
    fixed: { event: 'expired' },
    labels: {},
  },
  turnkey_session_reconcile_logout: {
    counter: SESSION_COUNTER,
    fixed: { event: 'reconcile_logout' },
    labels: { trigger: 'trigger' },
  },
  session_refresh: {
    counter: SESSION_REFRESH_COUNTER,
    fixed: {},
    labels: { trigger: 'trigger', outcome: 'outcome' },
  },
  wallet_export: {
    counter: WALLET_EXPORT_COUNTER,
    fixed: {},
    labels: { flow: 'flow', result: 'result' },
  },
};

export function isMirroredEvent(name: string): boolean {
  return Object.hasOwn(FUNNEL_MIRRORS, name);
}
